import {
  Alert,
  AlertDescription,
  AlertIcon, Box, Button, Divider, Grid, GridItem, Heading, HStack, Link, List, SimpleGrid, Text, VStack
} from "@chakra-ui/react";
import numeral from "numeral";
import React, { ReactElement } from "react";
import { NavLink } from "react-router-dom";
import { EXTENSION_DISPLAY_NAMES } from "../../../../utils/extensions";
import { DAMFeaturesEnum, FeatureNameMapping, FeaturesEnum, OtherFeaturesEnum } from '../../../../utils/features';
import { PlanDetails as OriginalPlanDetails, UserSessionResponse } from "../../../interfaces";
import { StandardPlan } from "../../../interfaces/UserSessionResponse";
import { BandwidthItem, CNAMEItem, CustomCheckIcon, ExtensionItem, HelpPopover, PurgeCacheItem, StorageItem, UserItem, VPUItem } from "./LinteItems";


type PlanDetails = OriginalPlanDetails & {
  isEnterprise?: boolean;
};

type Col = { id: string; content: ReactElement };

type Row = null | {
  label: string;
  info?: React.ReactNode;                   // popover (optional)
  values: Record<string, React.ReactNode>;  // planId -> cell
};

type Section = null | { title: string; rows: Row[] };

type Comparison = { columns: Col[]; sections: Section[] };

export const Plans = ({ user, onSelect }: {
  user: UserSessionResponse,
  onSelect: (plan: PlanDetails, currentPlan: OriginalPlanDetails) => void
}): React.ReactElement => {
  const DAM_ONLY_PLAN = user?.pricingPlan?.planType === "DAM_ONLY";

  const isUserOnStandardPublicPaidPlan = [
    user.availablePlans.freePlan.planId,
    user.availablePlans.damFreePlan.planId
  ].includes(user.availablePlans.currentPlan.planId)

  const plans: PlanDetails[] = [];

  // Push all current public standard plans
  if (DAM_ONLY_PLAN) {
    plans.push(user.availablePlans.damFreePlan);
    plans.push(user.availablePlans.damPremiumPlan);
    plans.push(user.availablePlans.damEnterprisePlan);
  } else {
    plans.push(user.availablePlans.freePlan);
    plans.push(user.availablePlans.litePlan);
    plans.push(user.availablePlans.premiumPlan);
    plans.push(user.availablePlans.enterprisePlan);
  }

  // Sort so that the free plan (isPaidPlan === false) comes first
  plans.sort((a, b) => {
    // 1. Free before paid
    if (!a.isPaidPlan && b.isPaidPlan) return -1;
    if (a.isPaidPlan && !b.isPaidPlan) return 1;

    // 2. Asc by fixedCost (undefined => bottom)
    if (a.isEnterprise || b.isEnterprise) return 1;
    const aCost = a.inclusionAndOverage?.fixedCost ?? Infinity;
    const bCost = b.inclusionAndOverage?.fixedCost ?? Infinity;
    return aCost - bCost;
  });

  return (
    <Box w={"1080px"}>
      {!isUserOnStandardPublicPaidPlan && (
        <Alert status="info" mb={6}>
          <AlertIcon />
          {/* <AlertTitle color={"imagekitGray.700"}>Plan details not available</AlertTitle> */}
          <AlertDescription color={"imagekitGray.900"}>
            You can view your current plan details on the <Link as={NavLink} to="/billing/subscriptions">subscription page</Link>.
          </AlertDescription>
        </Alert>
      )}

      <PlanGrid
        currentPlan={user.availablePlans.currentPlan}
        plans={plans}
        onSelect={onSelect}
        DAM_ONLY_PLAN={DAM_ONLY_PLAN}
      />

      <Heading mt={10} mb={6}>Detailed plan comparison</Heading>

      <PlanComparison plans={plans} DAM_ONLY_PLAN={DAM_ONLY_PLAN} />

    </Box>
  )
}

const buildComparison = (plans: PlanDetails[], DAM_ONLY_PLAN: boolean): Comparison => {
  const cols = plans.map(p => {
    return {
      id: p.planId,
      content: (
        <>
          <Text fontWeight={"semibold"}>{p.planName}</Text>
          {!p.isEnterprise && (
            <Text fontSize={"14px"}>
              {formatCurrency(p.inclusionAndOverage.fixedCost, p.currency)}/mo
            </Text>
          )}
          {p.isEnterprise && (
            <Text fontSize={"14px"}>
              Custom
            </Text>
          )}
        </>
      )
    }
  });

  const isStd = (p: PlanDetails): p is StandardPlan => !("isEnterprise" in p);

  const limitText = (limit: number) => {
    if (limit === -1) return "Unlimited";
    return limit;
  }

  const cellMap = (fn: (p: PlanDetails) => React.ReactNode) =>
    Object.fromEntries(plans.map(p => [p.planId, fn(p)]));

  const formatMP = (px: number) =>
    `${(px / 1_000_000)
      .toLocaleString(undefined, { maximumFractionDigits: 1 })
      .replace(/\.0$/, "")} MP`;

  const sections: Section[] = [
    // Inclusion and pricing
    {
      title: "Inclusions and pricing",
      rows: [
        DAM_ONLY_PLAN ? null : {
          label: "Bandwidth",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? <BandwidthItem
                  inclusion={p.inclusionAndOverage.bandwidth.inclusion}
                  overage={p.inclusionAndOverage.bandwidth.overage}
                  unit={p.inclusionAndOverage.bandwidth.unit}
                  showCheck={false}
                  padding={"0px"}
                />
                : "Custom"
            )
          }),
        },
        {
          label: "DAM storage",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? <StorageItem
                  inclusion={p.inclusionAndOverage.storage.inclusion}
                  overage={p.inclusionAndOverage.storage.overage}
                  unit={p.inclusionAndOverage.storage.unit}
                  showCheck={false}
                  padding={"0px"}
                />
                : "Custom"
            )
          }),
        },
        {
          label: "User seats",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? <UserItem
                  inclusion={p.inclusionAndOverage.userPrice.inclusion}
                  overage={p.inclusionAndOverage.userPrice.overage}
                  unit={p.inclusionAndOverage.userPrice.unit}
                  showCheck={false}
                  padding={"0px"}
                />
                : "Custom"
            )
          }),
        },
        DAM_ONLY_PLAN ? null : {
          label: "Video processing units",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? <VPUItem
                  inclusion={p.inclusionAndOverage.video.inclusion}
                  overage={p.inclusionAndOverage.video.overage}
                  unit={p.inclusionAndOverage.video.unit}
                  showCheck={false}
                  padding={"0px"}
                />
                : "Custom"
            )
          }),
        },
        {
          label: "Extensions",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? <ExtensionItem
                  inclusion={p.inclusionAndOverage.extension.inclusion}
                  overage={p.inclusionAndOverage.extension.overage}
                  unit={p.inclusionAndOverage.extension.unit}
                  showCheck={false}
                  padding={"0px"}
                />
                : "Custom"
            )
          }),
        },
        {
          label: "Purge cache API",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? <PurgeCacheItem
                  inclusion={p.inclusionAndOverage.purgeCacheAPIPrice.inclusion}
                  overage={p.inclusionAndOverage.purgeCacheAPIPrice.overage}
                  unit={p.inclusionAndOverage.purgeCacheAPIPrice.unit}
                  showCheck={false}
                  padding={"0px"}
                />
                : "Custom"
            )
          }),
        },
        {
          label: "Custom domain names",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? <CNAMEItem
                  inclusion={p.inclusionAndOverage.CNAMEPrice.inclusion}
                  overage={p.inclusionAndOverage.CNAMEPrice.overage}
                  unit={p.inclusionAndOverage.CNAMEPrice.unit}
                  showCheck={false}
                  padding={"0px"}
                />
                : "Custom"
            )
          }),
        },
      ],
    },
    // Limits
    {
      title: "Limits",
      rows: [
        {
          label: "External Origins",
          values: cellMap((p) => {
            return (
              p.isEnterprise ? "Custom" : limitText(p.limits.fixed.origins)
            )
          }),
        },
        {
          label: "URL Endpoints",
          values: cellMap((p) => {
            return (
              p.isEnterprise ? "Custom" : limitText(p.limits.fixed.endpoints)
            )
          }),
        },
        {
          label: "Performance Center URLs",
          values: cellMap((p) => {
            return (
              p.isEnterprise ? "Custom" : limitText(p.limits.fixed.performanceCenter.urls)
            )
          })
        },
        {
          label: "Public Links",
          values: cellMap((p) => {
            return (
              p.isEnterprise ? "Custom" : limitText(p.limits.fixed.publicLinks)
            )
          })
        },
        {
          label: "Image upload size",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? `${numeral(p.limits.fixed.uploadSize.image).format('0 ib').replace("iB", "B").toUpperCase()}`
                : "Custom"
            )
          }),
        },
        {
          label: "Video upload size",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? `${numeral(p.limits.fixed.uploadSize.video).format('0 ib').replace("iB", "B").toUpperCase()}`
                : "Custom"
            )
          }),
        },
        {
          label: "Audio upload size",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? `${numeral(p.limits.fixed.uploadSize.audio).format('0 ib').replace("iB", "B").toUpperCase()}`
                : "Custom"
            )
          }),
        },
        {
          label: "Raw file upload size",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? `${numeral(p.limits.fixed.uploadSize.raw).format('0 ib').replace("iB", "B").toUpperCase()}`
                : "Custom"
            )
          }),
        },
        {
          label: "Input image size",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? `${numeral(p.limits.fixed.input.size.image).format('0 ib').replace("iB", "B").toUpperCase()}`
                : "Custom"
            )
          }),
        },
        {
          label: "Input video size",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? `${numeral(p.limits.fixed.input.size.video).format('0 ib').replace("iB", "B").toUpperCase()}`
                : "Custom"
            )
          }),
        },
        {
          label: "Output image resolution",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? formatMP(p.limits.fixed.output.resolution.image)
                : "Custom"
            )
          }),
        },
        {
          label: "Output multi-frame image resolution",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? formatMP(p.limits.fixed.output.resolution.animatedImage ?? p.limits.fixed.output.resolution.image)
                : "Custom"
            )
          }),
        },
      ]
    },
    // AI-powered features
    {
      title: "AI-powered capabilities",
      rows: Object.values(EXTENSION_DISPLAY_NAMES).map((featureKey: string) => ({
        label: featureKey,
        values: cellMap((p) => {
          return <CustomCheckIcon />
        }),
      })),
    },
    // Media processing and general
    DAM_ONLY_PLAN ? null : {
      title: "Media processing and general",
      rows: [
        {
          label: "Image & video transformation and optimization",
          values: cellMap((p) => {
            return (
              <CustomCheckIcon />
            )
          }),
        },
        {
          label: "Raw file delivery - CSS, JS etc.",
          values: cellMap((p) => {
            return (
              <CustomCheckIcon />
            )
          }),
        },
        {
          label: "Integrated CDN with HTTPS",
          values: cellMap((p) => {
            return (
              <CustomCheckIcon />
            )
          }),
        },
      ].concat(
        OtherFeaturesEnum.filter((k: string) => k !== FeaturesEnum.VIDEO_PROCESSING).map((featureKey: string) => ({
          label: FeatureNameMapping[featureKey],
          values: cellMap((p) => {
            if (p.isEnterprise) return <CustomCheckIcon />;

            if (p.featuresList.includes(featureKey)) {
              return <CustomCheckIcon />;
            } else {
              return "-";
            }
          }),
        }))
      ).concat([
        {
          label: "Video processing in Europe region",
          values: cellMap((p) => {
            return (
              isStd(p)
                ? "-"
                : "Available on demand"
            )
          }),
        },
        {
          label: "Auto AVIF image format support",
          values: cellMap((p) => {
            if (p.isEnterprise || p.planId === "standard-pro-july-2025") return "Available on demand";
            return "-";
          }),
        }
      ])
    },
    // DAM features
    {
      title: "Digital Asset Management",
      rows: DAMFeaturesEnum.map((featureKey: string) => ({
        label: FeatureNameMapping[featureKey],
        values: cellMap((p) => {
          if (p.isEnterprise) return <CustomCheckIcon />;

          if (p.featuresList.includes(featureKey)) {
            return <CustomCheckIcon />;
          } else {
            return "-";
          }
        }),
      })),
    }
  ];

  return { columns: cols, sections };
};

const PlanComparison = ({ plans, DAM_ONLY_PLAN }: { plans: PlanDetails[], DAM_ONLY_PLAN: boolean }) => {

  const { columns, sections } = React.useMemo(() => buildComparison(plans, DAM_ONLY_PLAN), [plans]);

  const spacing = "8px 16px";

  return (
    <Box border="1px solid #E2E8F0" pb={4} borderRadius="8px" bg={"white"} fontFamily={"Inter"}>
      {/* header */}
      <Grid
        templateColumns={`250px repeat(${columns.length}, 1fr)`}
        pos="sticky"
        top={"86px"}
        bg={"white"}
        pb={4}
        pt={4}
        borderBottom="1px solid #E2E8F0"
        borderTopRadius={"8px"}
      >
        <Box p={spacing}>Key features & limits</Box>
        {columns.map(c =>
          <Box p={spacing} key={c.id}>
            {c.content}
          </Box>
        )}
      </Grid>

      {sections.map((section, i) => (
        section === null ? null :
          <React.Fragment key={i}>
            <Box fontWeight={"semibold"} m={spacing} mt={i === 0 ? 0 : 4} pt={4} borderTop={i === 0 ? "none" : "0.5px solid #E2E8F0"} borderBottom={"none"}>
              {section.title}
            </Box>

            {section.rows.map((row, j) => (
              row === null ? null :
                <Grid
                  key={`${i}-${j}`}
                  templateColumns={`250px repeat(${columns.length}, 1fr)`}
                  _hover={{ bg: "gray.50" }}
                >
                  <Box p={spacing}>
                    <HStack align="start" spacing={1}>
                      {row.info
                        ? <HelpPopover padding="0px" helpText={row.info}><Text>{row.label}</Text></HelpPopover>
                        : <Text>{row.label}</Text>}
                    </HStack>
                  </Box>
                  {columns.map(col => (
                    <Box p={spacing}>{row.values[col.id]}</Box>
                  ))}
                </Grid>
            ))}
          </React.Fragment>
      ))}
    </Box>
  );
};


export const PlanGrid = ({ currentPlan, plans, onSelect, DAM_ONLY_PLAN }: { currentPlan?: PlanDetails, plans: PlanDetails[], onSelect: Function, DAM_ONLY_PLAN: boolean }) => {
  const gridColumns = plans.length;

  return (
    <SimpleGrid columns={gridColumns} alignItems="flex-start" justifyContent="center" w={"full"}>
      {plans.map((plan, i) => {
        const isLastColumn = i === (gridColumns - 1);
        const isFirstColumn = i === 0;

        return (
          <GridItem
            h={"100%"}
            key={plan.planName}
            borderTopLeftRadius={isFirstColumn ? "8px" : "0"}
            borderTopRightRadius={isLastColumn ? "8px" : "0"}
            borderBottomLeftRadius={isFirstColumn ? "8px" : "0"}
            borderBottomRightRadius={isLastColumn ? "8px" : "0"}
            borderRight="1px solid #E2E8F0"
            borderLeft={isFirstColumn ? "1px solid #E2E8F0" : "none"}
            overflow="hidden"
            borderTop={"1px solid #E2E8F0"}
            borderBottom={"1px solid #E2E8F0"}
            bg={plan.isEnterprise === true ? "imagekitBlue.100" : "white"}
          >
            <PricingCard plan={plan} planOnLeft={plans[i - 1]} currentPlan={currentPlan} onSelect={onSelect} DAM_ONLY_PLAN={DAM_ONLY_PLAN} />
          </GridItem>
        );
      })}
    </SimpleGrid>
  );
}

function formatCurrency(value: number, currency: string) {
  // Falls back to plain number if Intl fails for an unknown currency code
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currency} ${value}`;
  }
}

function getPlanCTALabel({ isCurrentPlan, isFree, isEnterprise, currentPlan }: { isCurrentPlan: boolean, isFree: boolean, isEnterprise: boolean | undefined, currentPlan?: PlanDetails }): string {
  if (isCurrentPlan) {
    return "Current Plan";
  }
  if (isEnterprise) {
    return "Contact Sales";
  }
  if (!currentPlan) return "Get started"
  if (isFree) {
    if (currentPlan.isPaidPlan) return "Downgrade to Free Plan";
    return "Select Free Plan";
  }
  return "Select Plan";
}

const PricingCard = ({ plan, currentPlan, planOnLeft, onSelect, DAM_ONLY_PLAN }: { plan: PlanDetails, currentPlan?: PlanDetails, planOnLeft: PlanDetails, onSelect: Function, DAM_ONLY_PLAN: boolean }) => {
  const isFree = !plan.isPaidPlan;

  const isCurrentPlan = currentPlan ? (currentPlan?.planId === plan.planId) : false;

  /** ---- “$0 / mo” or “$89 / mo” ---- */
  const priceLabel = !plan.isEnterprise ? formatCurrency(plan.inclusionAndOverage.fixedCost, plan.currency) : "Custom";
  const cadence = '/ mo';

  /** ---- Optional tagline (“forever”, “+ pay-as-you-go”, etc.) ---- */
  const tagline = isFree
    ? ''
    : '+ pay-as-you-go';

  return (
    <Box>
      {/* Header */}
      <VStack align="start" spacing={0}>
        <Box bg={plan.isEnterprise ? "imagekitBlue.100" : "#f5f7f9"} w={"full"} borderBottom={"1px solid #e1e4eb"} p={"16px"}>
          <Text lineHeight={"1.5"} fontSize="18px" fontWeight={"500"}>{plan.planName}</Text>
        </Box>

        {/* Price row */}
        <HStack spacing={2} align="baseline" p={"16px"} width={"full"}>
          <Text fontSize={"32px"} fontWeight={"500"}>
            {priceLabel}
          </Text>

          {!plan.isEnterprise && (
            <>
              <Text fontSize={"14px"} color="imagekitGray.700">
                {cadence}
              </Text>

              {/* Tagline */}
              <Text fontSize={"14px"} color="imagekitGray.700">
                {tagline}
              </Text>
            </>
          )}


        </HStack>

        <Text p={"16px"} fontSize={"14px"} fontFamily={"Inter"} color="imagekitGray.700"
          lineHeight={`21px`}
          h={`${4 * 21}px`}>
          {plan.description ?? ""}
        </Text>

        {/* CTA */}
        <Box p={"16px"} width={"full"}>
          <Button
            disabled={isCurrentPlan}
            size="lg"
            colorScheme={(isFree && currentPlan && currentPlan.isPaidPlan) ? "appRed" : "appBlue"}
            isFullWidth={true}
            variant={plan.isEnterprise ? "outline" : "solid"}
            onClick={() => {
              onSelect(plan, currentPlan)
            }}
            cursor={isCurrentPlan ? 'not-allowed' : 'pointer'}
          >
            {getPlanCTALabel({ isCurrentPlan, isFree, isEnterprise: plan.isEnterprise, currentPlan })}
          </Button>
        </Box>

      </VStack>

      {/* Included limits (bandwidth, storage …) */}
      {!plan.isEnterprise && (
        <>
          <Box>
            <Text p={"16px"} pt={"8px"} fontFamily={"Inter"} fontSize={"14px"} color="imagekitGray.600" fontWeight={"500"} pb={"8px"}>
              Inclusions:
            </Text>

            {DAM_ONLY_PLAN ? (
              <>
                <StorageItem inclusion={plan.inclusionAndOverage.storage.inclusion}
                  overage={plan.inclusionAndOverage.storage.overage}
                  unit={plan.inclusionAndOverage.storage.unit}
                  helpText={plan.inclusionAndOverage.storage.helpText ?? null} />
                <UserItem inclusion={plan.inclusionAndOverage.userPrice.inclusion}
                  overage={plan.inclusionAndOverage.userPrice.overage}
                  unit={plan.inclusionAndOverage.userPrice.unit}
                  helpText={plan.inclusionAndOverage.userPrice.helpText ?? null} />
              </>

            ) : (
              <>
                <BandwidthItem inclusion={plan.inclusionAndOverage.bandwidth.inclusion}
                  overage={plan.inclusionAndOverage.bandwidth.overage}
                  unit={plan.inclusionAndOverage.bandwidth.unit}
                  helpText={plan.inclusionAndOverage.bandwidth.helpText ?? null}
                />
                <StorageItem inclusion={plan.inclusionAndOverage.storage.inclusion}
                  overage={plan.inclusionAndOverage.storage.overage}
                  unit={plan.inclusionAndOverage.storage.unit}
                  helpText={plan.inclusionAndOverage.storage.helpText ?? null} />
                <VPUItem inclusion={plan.inclusionAndOverage.video.inclusion}
                  overage={plan.inclusionAndOverage.video.overage}
                  unit={plan.inclusionAndOverage.video.unit}
                  helpText={plan.inclusionAndOverage.video.helpText ?? null} />
                <ExtensionItem inclusion={plan.inclusionAndOverage.extension.inclusion}
                  overage={plan.inclusionAndOverage.extension.overage}
                  unit={plan.inclusionAndOverage.extension.unit}
                  helpText={plan.inclusionAndOverage.extension.helpText ?? null} />
                <UserItem inclusion={plan.inclusionAndOverage.userPrice.inclusion}
                  overage={plan.inclusionAndOverage.userPrice.overage}
                  unit={plan.inclusionAndOverage.userPrice.unit}
                  helpText={plan.inclusionAndOverage.userPrice.helpText ?? null} />
                <PurgeCacheItem inclusion={plan.inclusionAndOverage.purgeCacheAPIPrice.inclusion}
                  overage={plan.inclusionAndOverage.purgeCacheAPIPrice.overage}
                  unit={plan.inclusionAndOverage.purgeCacheAPIPrice.unit} 
                  helpText={plan.inclusionAndOverage.purgeCacheAPIPrice.helpText ?? null} />
              </>
            )}
          </Box>
          <Box p={"16px"}>
            <Divider borderColor={"imagekitGray.300"} />
          </Box>
        </>
      )}

      {/* Details */}
      <Box pb={4}>
        <Text p={"16px"} pt={"8px"} fontFamily={"Inter"} fontSize={"14px"} color="imagekitGray.600" fontWeight={"500"} pb={"8px"}>
          {planOnLeft ? `Everything in ${planOnLeft.planName}, plus:` : "What's included:"}
        </Text>
        <List spacing={3}>
          {plan.planDetails.map((detail, index) => (
            <HelpPopover key={index} padding="8px 16px">
              <CustomCheckIcon />
              <Text>
                {detail.text.replace(/\.$/, '')}
              </Text>
            </HelpPopover>
          ))}
        </List>
      </Box>
    </Box>
  );
};



